import { useCallback, useEffect, useId, useState } from 'react';
import AirConditioning from '@engineering/icons/air-conditioning';
import Automatic from '@engineering/icons/automatic';
import CarTypes from '@engineering/icons/car-types';
import CityStation from '@engineering/icons/city-station';
import Airport from '@engineering/icons/airport';
import Doors from '@engineering/icons/doors';
import RentalCar from '@engineering/icons/services/rental-car';
import Seats from '@engineering/icons/seats';
import Transmission from '@engineering/icons/transmission';
import { useResetOnBfcacheRestore } from '@silvertours/front-shared';
import { useLocale, useCarImage, useHost, usePrice } from '../Runtime';

import {
  Wrapper,
  Sample,
  Image,
  ImageFallback,
  FallbackIcon,
  Caption,
  CarAttributes,
  CarAttribute,
  CarAttributeEmpty,
  StyledIcon,
  Stations,
  CarCategory,
  Price,
  PriceValue,
  PriceSuffix,
  CallToAction,
  StyledArrowNext,
} from './SerpOffer.styles';

// `category` arrives as the lowercase CarType key (e.g. "suv"); CarTypes
// pictograms are keyed in PascalCase and don't cover every CarType (e.g.
// "mini"), so an unmapped category falls back to the generic pictogram.
const getCategoryIcon = (category: string) => {
  const key = (category.charAt(0).toUpperCase() +
    category.slice(1)) as keyof typeof CarTypes;
  return CarTypes[key] ?? RentalCar;
};

type ImageStage = 'webp' | 'original' | 'fallback' | 'icon';

const NEXT_IMAGE_STAGE: Record<ImageStage, ImageStage> = {
  webp: 'original',
  original: 'fallback',
  fallback: 'icon',
  icon: 'icon',
};

const CAR_IMAGE_PATH = '/static/default/global/graphic/car/';

// Our image hosts serve the supplier car photos (`<supplier>/<size>/<id>.jpg`)
// also as WebP at the same path, at about a quarter of the size. If a WebP
// variant is missing, `onError` drops to the JPEG. Returns `undefined` for
// every other source, external hosts included (the BFF passes supplier
// image URLs through unchanged), so that it starts at the JPEG.
const getWebpCarImage = (imageUrl: string, ownOrigins: string[]) => {
  try {
    const url = new URL(imageUrl);

    if (
      !ownOrigins.includes(url.origin) ||
      !url.pathname.startsWith(CAR_IMAGE_PATH) ||
      !url.pathname.endsWith('.jpg')
    ) {
      return undefined;
    }

    url.pathname = url.pathname.replace(/\.jpg$/, '.webp');

    return url.toString();
  } catch {
    return undefined;
  }
};

type SerpOfferProps = {
  image: string;
  fallbackImage: string;
  category: string;
  transmission: string;
  price: number;
  currency: string;
  carCategory: string;
  carName: string;
  seats: number;
  doors: string;
  airConditioning: boolean;
  city: string;
  isAirport: boolean;
  onClick?: () => void;
  translations: {
    seats: string;
    doors: string;
    airConditioning: string;
    transmission: string;
    transmissionManual: string;
    transmissionAutomatic: string;
    pricePrefix: string;
    priceSuffix: string;
    searchButtonText: string;
  };
};

const SerpOffer = ({
  image,
  fallbackImage,
  category,
  transmission,
  price,
  currency,
  carCategory,
  carName,
  seats,
  doors,
  airConditioning,
  city,
  isAirport,
  onClick,
  translations,
}: SerpOfferProps) => {
  const { locale } = useLocale();
  const [loading, setLoading] = useState(false);
  const imageUrl = useCarImage(image);
  const fallbackImageUrl = useCarImage(fallbackImage);
  const { baseUrl, baseImageUrl } = useHost();
  const webpImageUrl = getWebpCarImage(imageUrl, [baseImageUrl, baseUrl]);
  const initialImageStage = webpImageUrl ? 'webp' : 'original';
  // The primary offer photo can 404 (suppliers rotate/retire images); on
  // error we drop from its WebP variant to the JPEG, then to the generic
  // per-category photo, and if that also fails, to a bundled pictogram
  // that can't 404.
  const [imageStage, setImageStage] = useState<ImageStage>(initialImageStage);
  const id = `serpOffer-${useId()}`;

  // If the offer's image URLs change (e.g. the same card is reused for a
  // different offer), retry the primary photo instead of keeping a stage
  // reached because a previous offer's photo had failed.
  useEffect(() => {
    setImageStage(initialImageStage);
  }, [initialImageStage, imageUrl, fallbackImageUrl]);

  useResetOnBfcacheRestore(() => setLoading(false));

  const getLabel = useCallback(
    (label: string, value: string) => {
      const translation = translations[label as keyof typeof translations];
      return translation === value ? undefined : translation;
    },
    [translations],
  );

  const handleClick = () => {
    if (onClick && !loading) {
      setLoading(true);
      onClick();
    }
  };

  const attributes = [
    { label: 'seats', value: seats, icon: Seats },
    { label: 'doors', value: doors, icon: Doors },
    {
      label: 'transmission',
      value:
        transmission === 'manual'
          ? translations.transmissionManual
          : translations.transmissionAutomatic,
      icon: transmission === 'manual' ? Transmission : Automatic,
    },
    ...(airConditioning
      ? [
          {
            label: 'airConditioning',
            icon: AirConditioning,
            value: translations.airConditioning,
          },
        ]
      : []),
  ];

  const formattedPrice = usePrice({ amount: price, currency }, locale);

  return (
    <Wrapper onClick={handleClick} hasAction={!!onClick}>
      <CarCategory>{carCategory}</CarCategory>
      <Price value={price}>
        {translations.pricePrefix} <PriceValue>{formattedPrice}</PriceValue>
        <PriceSuffix>{translations.priceSuffix}</PriceSuffix>
      </Price>
      <Sample aria-labelledby={`${id}-image`}>
        {imageStage === 'icon' ? (
          <ImageFallback id={`${id}-image`} aria-label={carName}>
            <FallbackIcon as={getCategoryIcon(category)} />
          </ImageFallback>
        ) : (
          <Image
            key={imageStage}
            src={
              {
                webp: webpImageUrl ?? imageUrl,
                original: imageUrl,
                fallback: fallbackImageUrl,
              }[imageStage]
            }
            alt={carName}
            fill
            id={`${id}-image`}
            onError={() => setImageStage(stage => NEXT_IMAGE_STAGE[stage])}
          />
        )}
      </Sample>
      <Caption aria-hidden="true">{carName}</Caption>
      <CarAttributes>
        {carCategory !== 'Transporter' &&
          attributes.map(({ label, value, icon: Icon }) => (
            <CarAttribute key={label}>
              <StyledIcon
                as={Icon}
                aria-hidden={
                  getLabel(label, value.toString()) ? undefined : 'true'
                }
                aria-label={getLabel(label, value.toString())}
              />
              <data value={value}>{value}</data>
            </CarAttribute>
          ))}
        {carCategory === 'Transporter' && <CarAttributeEmpty />}
      </CarAttributes>
      <Stations>
        <StyledIcon as={isAirport ? Airport : CityStation} />
        <span>{city}</span>
      </Stations>
      <CallToAction
        icon={<StyledArrowNext />}
        size="small"
        loading={loading}
        aria-label={translations.searchButtonText}
      />
    </Wrapper>
  );
};

export { SerpOffer };
